import React, { useState } from "react";
import { Calendar, Clock, ArrowLeft, Search, Tag } from "lucide-react";
import { BLOG_POSTS, CONTACT } from "../data/mock";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ContactModal } from "../components/ContactModal";

export default function BlogPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("All");

  const categories = ["All", ...new Set(BLOG_POSTS.map((p) => p.category))];

  const filtered = BLOG_POSTS.filter((p) => {
    const matchTag = activeTag === "All" || p.category === activeTag;
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchTag && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar onGetQuote={() => setModalOpen(true)} />
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Hero */}
      <div className="bg-[#1a1a1a] pt-24 pb-14 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <a href="/" className="inline-flex items-center gap-2 text-[#C9A84C] text-sm mb-6 hover:gap-3 transition-all">
            <ArrowLeft size={14} /> Back to Home
          </a>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4">
            Interior Design Blog
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Expert tips, design inspiration, and ideas for your Hyderabad home
          </p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white/10 border border-white/20 text-white placeholder:text-gray-400 rounded-full pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[#C9A84C]"
              data-testid="blog-search"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveTag(cat)}
              className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border transition-all ${
                activeTag === cat
                  ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                  : "bg-white border-gray-200 text-[#2D2D2D] hover:border-[#C9A84C] hover:text-[#C9A84C]"
              }`}
              data-testid={`blog-category-${cat}`}
            >
              <Tag size={11} />
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No articles found.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group"
                onClick={() => window.location.href = `/blog/${post.slug}`}
                data-testid={`blog-article-${post.id}`}
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold px-3 py-1 rounded-full mb-3">
                    {post.category}
                  </span>
                  <h2 className="font-serif text-lg font-bold text-[#1a1a1a] mb-2 leading-snug line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-gray-500 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100">
                    <span className="flex items-center gap-1.5"><Calendar size={11} />{post.date}</span>
                    <span className="flex items-center gap-1.5"><Clock size={11} />{post.readTime}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <Footer onGetQuote={() => setModalOpen(true)} />
    </div>
  );
}
